/**
 * Created by Claude on 2026-02-04
 * WindowComponent - provides window functionality via ComponentMap
 */

class WindowComponent extends BaseComponent {
    constructor(bundles, loadTasks, options, lazyUrls) {
        super(options);
        this._loaded = false;
        this.bundles = bundles || [];
        this.lazyUrls = lazyUrls || [];
        this._loadTasks = loadTasks || [];
    }

    onAttach() {
        this.node.setAnchorPoint(0.5, 0.5);
        this.node.setLocalZOrder(WindowComponent.WINDOWS_ZORDER);
        this.node.openTime = Date.now();

        // Esc / hardware back. CleverappsWindow assigns its own in onWindowLoaded (and skips it for
        // closeButton: false); DialogueView declares one on its prototype. Only nodes with neither -
        // prefab windows - need one here, and without it ReturnAction had nothing to call on them.
        // Opt out with `closeByHardwareBackButton: false`.
        if (!(this.node instanceof CleverappsWindow) && !this.node.closeByHardwareBackButton
            && this.options.closeByHardwareBackButton !== false) {
            this.node.closeByHardwareBackButton = this.node.createListener(() => {
                if (cleverapps.focusManager.checkEventNode(this.node)) {
                    this.closeWindow();
                }
            });
        }

        if (!cleverapps.focusManager.isFocused() && ["promptwindow"].indexOf(this.node.name) === -1) {
            cleverapps.focusManager.debugMessage("Trying to show window without user focus!");
        }

        cleverapps.Scenes.GetRunningScene().addChild(this.node);

        const topWindow = cleverapps.focusManager.currentWindow();
        if (topWindow) {
            const topWindowComponent = ComponentMap.get(topWindow, WindowComponent);
            topWindowComponent.hideWindow();
        }

        WindowComponent.showLoading();

        cleverapps.focusManager.addWindow(this.node);
        cleverapps.focusManager.clearUpdateTimeout();

        if (!(this.node instanceof CleverappsWindow)) {
            this.attachSoundEffects();
        }

        this.displayWindow();

        if (connector.platform.oneOf(connector.CRAZY)) {
            connector.platform.gameplayStop();
            this.node.addCleaner(() => connector.platform.gameplayStart());
        }

        if (!topWindow) {
            cleverapps.eventBus.trigger("openWindow");
        }

        const start = Date.now();
        const windowArgs = this.node._windowArgs || [];

        this.node.notLoaded = true;

        const onSuccess = this.node.createListener(() => {
            if (this.node instanceof CleverappsWindow) {
                this.node.onResizeWhileNotLoaded();
            }
            this.setLoaded();
            this.node.onWindowLoaded(...windowArgs);
            if (this.node instanceof CleverappsWindow) {
                delete this.node.notLoaded;

                this.node.onAfterResize();
            }

            this.displayWindow();

            cleverapps.ConsoleStream.sendLoadingTime(`window ${this.node.name} ${Date.now() - start}ms`);
        });

        let retries = 0;

        const onFailure = this.node.createListener((errors) => {
            cleverapps.focusManager.display({
                focus: "LoadWindowFailureRestart",
                stack: true,
                action: (f) => {
                    ErrorHandler.showRestartWindow({
                        errors,
                        retries,
                        onRetry: () => {
                            retries++;
                            f();
                            this.unload();
                            this.load(onSuccess, onFailure);
                        }
                    });
                }
            });
        });

        this.load(onSuccess, onFailure);
    }

    load(onSuccess, onFailure) {
        const tasks = [
            (onDone, onFail) => WindowComponent.loadBundles(this.bundles, onDone, onFail, this.lazyUrls),
            (onDone) => setTimeout(onDone, 0),
            ...this._loadTasks
        ];

        onSuccess = cleverapps.wait(tasks.length, onSuccess);
        tasks.forEach((task) => task(onSuccess, onFailure));
    }

    setLoaded() {
        this._loaded = true;
    }

    unload() {
        cleverapps.bundleLoader.deleteBundles(this.bundles);
    }

    isWindowLoaded() {
        return this._loaded;
    }

    displayWindow() {
        if (!this.isWindowLoaded() || cleverapps.focusManager.currentWindow() !== this.node) {
            return;
        }

        WindowComponent.hideLoading();
        cleverapps.focusManager.updateControlsVisibility();

        this.node.setVisible(true);

        this.node.focusState.eventNodes.length = 0;

        if (!this.node.closed) {
            const eventNodes = this.node.getEventNodes ? this.node.getEventNodes() : [this.node];
            this.node.focusState.eventNodes.push(...eventNodes);
        }

        if (this.node.withShadow) {
            cleverapps.shadow.show();
        } else {
            cleverapps.shadow.hide();
        }

        if (this.node.popUpAnimation) {
            this.node.popUpAnimation();
        }

        if (this.node.onShow) {
            this.node.onShow();
        }

        cc.Base.PerformRecursive(this.node, (node) => {
            ComponentMap.callHooks(node, "onShow");
        });

        cleverapps.eventBus.trigger("window_shown", { windowName: this.node.name });
        cleverapps.tutorial.trigger("window_shown", { windowName: this.node.name });
    }

    closeWindow() {
        if (this.node.closed) {
            cleverapps.throwAsync(`Closing already closed window: ${this.node.name}`);
            return;
        }
        this.node.closed = true;

        const isLoaded = this.isWindowLoaded();

        if (isLoaded && this.node.hideComponents) {
            this.node.hideComponents();
        }

        const state = this.node.focusState;

        if (this.node === cleverapps.focusManager.currentWindow()) {
            state.eventNodes.length = 0;
        }

        new ActionPlayer([
            (f) => {
                if (isLoaded && this.node.beforeCloseAnimation) {
                    this.node.beforeCloseAnimation(f);
                } else {
                    f();
                }
            },

            (f) => {
                if (isLoaded && this.node === cleverapps.focusManager.currentWindow() && this.node.closeAnimation) {
                    this.node.closeAnimation(f);
                } else {
                    f();
                }
            },

            (f) => {
                if (isLoaded && this.node.shareCheckBoxModel) {
                    this.node.shareCheckBoxModel.onWindowClose(f);
                } else {
                    f();
                }
            },

            (f) => {
                const isActiveWindow = (this.node === cleverapps.focusManager.currentWindow());

                cleverapps.focusManager.deleteWindow(this.node);

                if (!isActiveWindow) {
                    f();
                    return;
                }

                if (!this.isWindowLoaded()) {
                    WindowComponent.hideLoading();
                }

                const topWindow = cleverapps.focusManager.currentWindow();

                if (topWindow) {
                    WindowComponent.showLoading();

                    ComponentMap.get(topWindow, WindowComponent).displayWindow();
                } else {
                    WindowComponent.hideLoading();

                    const scene = cleverapps.Scenes.GetRunningScene();
                    if (scene && scene.onShow) {
                        scene.onShow();
                    }

                    setTimeout(() => {
                        if (!cleverapps.focusManager.currentWindow()) {
                            cleverapps.eventBus.trigger("closeWindows");
                        }
                    }, 200);
                }

                f();
            },

            (f) => {
                if (isLoaded) {
                    if (this.node.onClose) {
                        this.node.onClose();
                    }

                    cc.Base.PerformRecursive(this.node, (node) => {
                        ComponentMap.callHooks(node, "onClose");
                    });
                }

                this.node.removeFromParent();

                this.unload();

                setTimeout(f, 0);
            },

            (f) => {
                cleverapps.focusManager.notifyPopWindow(state);

                cleverapps.eventBus.trigger("window_closed", { windowName: this.node.name });
                cleverapps.tutorial.trigger("window_closed", { windowName: this.node.name });

                f();
            }
        ]).play();
    }

    hideWindow() {
        if (cleverapps.focusManager.currentWindow() !== this.node) {
            return;
        }

        this.node.setVisible(false);

        if (this.node.withShadow) {
            cleverapps.shadow.hide();
        }

        if (this.isWindowLoaded()) {
            if (this.node.onHide) {
                this.node.onHide();
            }

            cc.Base.PerformRecursive(this.node, (node) => {
                ComponentMap.callHooks(node, "onHide");
            });
        }
    }

    attachSoundEffects() {
        const options = this.node.params || this.options;

        const effectsMap = {};
        if (options.openSound) {
            effectsMap["ui.window.open"] = { bundle: options.openSound.bundleName, sound: options.openSound.key };
        } else if (bundles.main.urls.open_window_effect) {
            effectsMap["ui.window.open"] = { bundle: "main", sound: "open_window_effect" };
        }
        if (bundles.main.urls.close_window_effect) {
            effectsMap["ui.window.close"] = { bundle: "main", sound: "close_window_effect" };
        }
        if (options.showSound) {
            effectsMap["ui.window.show"] = { bundle: options.showSound.bundleName, sound: options.showSound.key };
        }
        if (Object.keys(effectsMap).length) {
            ComponentMap.attach(this.node, new EffectsComponent({ map: effectsMap }));
        }
    }

    onResize() {
        const sceneSize = cleverapps.resolution.getSceneSize();
        this.node.setContentSize(sceneSize.width, sceneSize.height);
        this.node.setPosition(sceneSize.width / 2, sceneSize.height / 2);
    }
}

WindowComponent.loadBundles = function (bundles, onSuccess, onFailure, lazyUrls) {
    if (bundles.length === 0) {
        onSuccess();
        return;
    }

    cleverapps.bundleLoader.loadBundles(bundles, { onSuccess, onFailure, lazyUrls });
};

WindowComponent.hideLoading = function () {
    cleverapps.shadow.hide();
    cleverapps.shadow.moveDown();
    cleverapps.loading.hide();
};

WindowComponent.showLoading = function () {
    cleverapps.shadow.moveUp();
    cleverapps.shadow.show(true);
    cleverapps.loading.show();
};

WindowComponent.WINDOWS_ZORDER = 22;
